import { api, ApiError } from "../api.js";
import { esc } from "../utils.js";
import { navigate } from "../app.js";
import { searchPickerHtml, attachSearchPicker, pickerValue } from "../searchPicker.js";

/** Doubles as both "plan a new dive" (#/create-dive-plan) and "edit an
 * existing one" (#/dive-plan/:id/edit) — params.id, when present,
 * switches this into edit mode and pre-fills the form. */
export async function render(container, params, query) {
  const editingId = params?.id;
  const initialCommunityId = query?.get("community_id");
  // Planning from a dive site page: the site is the context you came
  // from, so it arrives prefilled rather than typed again.
  const initialSiteId = query?.get("dive_site_id");
  let existing = null;

  if (editingId) {
    container.innerHTML = `<div class="text-center py-5"><div class="spinner-border text-primary"></div></div>`;
    try {
      existing = await api.get(`/dive-plans/${editingId}`);
    } catch (err) {
      container.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>Dive event not found.</div></div>`;
      return;
    }
  }

  // Native <input type="date"/"time"> want "YYYY-MM-DD" / "HH:MM" in the
  // *local* timezone, not the ISO string's UTC representation.
  let dateVal = "";
  let timeVal = "08:00";
  if (existing) {
    const d = new Date(existing.planned_date);
    const pad = (n) => String(n).padStart(2, "0");
    dateVal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    timeVal = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  container.innerHTML = `
    <h1 class="display-heading h3 mb-4">${editingId ? "Edit Dive Event" : "Plan a Dive"}</h1>
    <form id="plan-form">
      <div class="mb-3">
        <label class="form-label" for="plan-title">Title</label>
        <input type="text" class="form-control" id="plan-title" required value="${existing ? esc(existing.title) : ""}" />
      </div>
      <div class="mb-3">
        ${searchPickerHtml("plan-site", {
          label: "Dive site (optional)",
          placeholder: "Type to search dive sites",
          value: existing?.dive_site?.id ?? initialSiteId ?? "",
          valueLabel: existing?.dive_site?.name ?? "",
          // An event can be announced on a date and a boat, with the
          // site picked on the conditions. Leaving it empty says so
          // rather than forcing a guess that has to be corrected later.
          hint: "Leave empty if the site is still to be determined.",
        })}
        <a href="#/create-dive-site" class="btn btn-sm btn-outline-secondary mt-2" id="add-site-link">
          <i class="bi bi-plus-lg me-1"></i>Add a dive site
        </a>
      </div>
      <div class="mb-3">
        ${searchPickerHtml("plan-community", {
          label: "Organize under a cohort (optional)",
          placeholder: "Type to search your cohorts",
          value: existing?.community?.id ?? initialCommunityId ?? "",
          valueLabel: existing?.community?.name ?? "",
          hint: "Only cohorts you have joined. Leave empty for no cohort.",
        })}
      </div>
      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label" for="plan-date">Date</label>
          <input type="date" class="form-control" id="plan-date" required value="${dateVal}" />
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label" for="plan-time">Time</label>
          <input type="time" class="form-control" id="plan-time" required value="${timeVal}" />
        </div>
      </div>
      <div class="mb-3">
        <label class="form-label" for="plan-max">Max participants (optional)</label>
        <input type="number" min="1" class="form-control" id="plan-max" value="${existing?.max_participants ?? ""}" />
      </div>
      <div class="mb-3">
        <label class="form-label" for="plan-description">Description (optional)</label>
        <textarea class="form-control" id="plan-description" rows="3">${existing ? esc(existing.description || "") : ""}</textarea>
      </div>
      <div id="plan-error" class="text-danger small mb-3 d-none"></div>
      <div class="d-flex gap-2">
        <button type="submit" class="btn btn-primary" id="plan-submit">${editingId ? "Save" : "Create"}</button>
        <button type="button" class="btn btn-outline-secondary" id="plan-cancel">Cancel</button>
      </div>
    </form>`;

  // Searched rather than pre-loaded: the list endpoints paginate, so a
  // dropdown of every site silently stopped at the first page.
  const sitePicker = attachSearchPicker(container, { id: "plan-site", path: "/dive-sites" });
  // Only your own, as on the post form — organizing an event under a
  // cohort you are not in is refused by the API.
  const cohortPicker = attachSearchPicker(container, {
    id: "plan-community",
    path: "/cohorts",
    query: { mine: 1 },
  });
  container.querySelector("#plan-cancel").addEventListener("click", () => history.back());

  container.querySelector("#plan-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    const errorEl = container.querySelector("#plan-error");
    errorEl.classList.add("d-none");
    const submitBtn = container.querySelector("#plan-submit");

    const dateInput = container.querySelector("#plan-date").value;
    const timeInput = container.querySelector("#plan-time").value;
    if (!dateInput || !timeInput) {
      errorEl.textContent = "Please fill in a date and time.";
      errorEl.classList.remove("d-none");
      return;
    }
    // An empty box means "to be determined", deliberately. A box with
    // text in it but nothing chosen is the other thing — a search the
    // reader never finished — and submitting that as undecided would
    // quietly throw away what they meant.
    const siteTyped = container.querySelector("#plan-site-input").value.trim();
    if (siteTyped && pickerValue(container, "plan-site") == null) {
      errorEl.textContent =
        "Pick a dive site from the list, or clear the box to leave it to be determined.";
      errorEl.classList.remove("d-none");
      return;
    }
    submitBtn.disabled = true;

    try {
      const maxVal = container.querySelector("#plan-max").value;
      // Convert the picked local wall-clock time to UTC before sending —
      // the backend now stores/returns everything in UTC (timezone-aware
      // columns) and expects clients to convert, matching the standard
      // "store UTC, display local" approach. new Date(`${dateInput}T${timeInput}:00`)
      // (no offset suffix) is parsed as local time per spec, so
      // .toISOString() correctly converts it to the matching UTC instant.
      // formatDateTime() in utils.js already parses the UTC value back
      // and renders via toLocaleString(), so the round trip is symmetric.
      const plannedDateUtc = new Date(`${dateInput}T${timeInput}:00`).toISOString();
      const body = {
        title: container.querySelector("#plan-title").value.trim(),
        description: container.querySelector("#plan-description").value.trim() || null,
        dive_site_id: pickerValue(container, "plan-site"),
        planned_date: plannedDateUtc,
        community_id: pickerValue(container, "plan-community"),
        max_participants: maxVal ? Number(maxVal) : null,
      };
      const plan = editingId
        ? await api.patch(`/dive-plans/${editingId}`, body)
        : await api.post("/dive-plans", body);
      navigate(`/dive-plan/${plan.id}`);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : "Something went wrong.";
      errorEl.classList.remove("d-none");
    } finally {
      submitBtn.disabled = false;
    }
  });

  // Last, deliberately. Binding the form before this await means there
  // is no window in which the markup exists unbound — a second render
  // landing mid-await would otherwise replace the form and leave both
  // renders binding to the same surviving one, submitting twice.
  // Nothing above depends on the labels: the pickers already hold the
  // ids, and submit reads them when it runs.
  await Promise.all([sitePicker?.resolveInitial(), cohortPicker?.resolveInitial()]);
}
